import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import BottomSheet from '@/pages/profile/components/BottomSheet';
import { useToast } from '@/components/base/Toast';
import { supabase } from '@/lib/supabase';
import { logout } from '@/lib/session';
import { useLiveAssets } from '@/hooks/useLiveAssets';

interface ProfileData {
  phone: string;
  name: string;
  member_level: string;
  verified: boolean;
  asset_value: number;
  gold_price: number;
  holding_gold: number;
  order_count: number;
  processing_count: number;
  email: string;
  role: string;
}

const quickActions = [
  { label: '资金提取', desc: '提取账户余额', icon: 'ri-wallet-3-line', path: '/profile/withdraw' },
  { label: '申请提货', desc: '提取实物黄金', icon: 'ri-truck-line', path: '/profile/pickup' },
  { label: '资金存入', desc: '充值账户余额', icon: 'ri-safe-2-line', path: '/deposit' },
];

const menuItems = [
  { label: '实名认证', desc: '', icon: 'ri-shield-user-line', path: '/profile/verify', verifiedBadge: true },
  { label: '我的金库', desc: '查看金条存储', icon: 'ri-building-2-line', path: '/profile/vault' },
  { label: '收货地址', desc: '管理收货地址', icon: 'ri-map-pin-line', path: '/profile/address' },
  { label: '银行卡管理', desc: '管理银行卡', icon: 'ri-bank-card-line', path: '/profile/bank' },
  { label: '资金流水', desc: '收支明细记录', icon: 'ri-exchange-line', path: '/profile/transactions' },
  { label: '消息中心', desc: '查看消息通知', icon: 'ri-notification-3-line', path: '/profile/messages' },
  { label: '帮助中心', desc: '常见问题解答', icon: 'ri-question-line', path: '/profile/help' },
  { label: '设置', desc: '账号与安全', icon: 'ri-settings-3-line', path: '/profile/settings' },
];

export default function ProfilePage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [showLogout, setShowLogout] = useState(false);
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const liveAssets = useLiveAssets();
  const [assetChange, setAssetChange] = useState<number | null>(null);
  const prevTotalRef = useRef<number>(0);
  const changeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 总资产：统一使用 liveAssets.totalAssets（包含可用+已用+浮动盈亏+实物）
  const totalAssets = liveAssets.totalAssets || 0;

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const userId = sessionData.session?.user?.id;
        if (!userId) return;

        const { data: profileData, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .maybeSingle();

        if (error) throw error;

        if (mounted && profileData) {
          const phoneFromMeta = sessionData.session?.user?.user_metadata?.phone as string | undefined;
          setProfile({
            phone: profileData.phone || phoneFromMeta || '',
            name: profileData.name || '',
            member_level: profileData.member_level || 'VIP会员',
            verified: profileData.verified ?? true,
            asset_value: Number(profileData.asset_value) || 0,
            gold_price: Number(profileData.gold_price) || 0,
            holding_gold: Number(profileData.holding_gold) || 0,
            order_count: Number(profileData.order_count) || 0,
            processing_count: Number(profileData.processing_count) || 0,
            email: sessionData.session?.user?.email || '',
            role: profileData.role || 'user',
          });
        }
      } catch {
        // 数据加载失败时保持默认
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  // 检测总资产变化，显示涨跌提示（首次加载不显示）
  useEffect(() => {
    const current = liveAssets.totalAssets || 0;
    const prev = prevTotalRef.current;
    if (prev !== 0 && Math.abs(current - prev) > 0.01) {
      const diff = current - prev;
      setAssetChange(diff);
      if (changeTimerRef.current) clearTimeout(changeTimerRef.current);
      changeTimerRef.current = setTimeout(() => setAssetChange(null), 3000);
    }
    prevTotalRef.current = current;
    return () => {
      if (changeTimerRef.current) clearTimeout(changeTimerRef.current);
    };
  }, [liveAssets.totalAssets]);

  const displayName = profile?.phone || profile?.email || '';

  const stats = [
    { label: '预约订单', value: profile?.order_count ?? 0, unit: '笔' },
    { label: '履约中', value: profile?.processing_count ?? 0, unit: '笔' },
    { label: '持有黄金', value: liveAssets.holdingGold ?? 0, unit: 'g' },
  ];

  const handleLogout = async () => {
    setShowLogout(false);
    await logout();
    showToast('已安全退出', 'info');
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-brown-50 pb-28">
      {/* Header */}
      <div className="bg-gradient-to-br from-brown-900 via-brown-800 to-brown-950 px-5 pt-safe pb-10 relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full border border-gold-400/10"></div>
        <div className="absolute top-20 right-10 w-16 h-16 rounded-full border border-gold-400/10"></div>

        <div className="relative flex items-center justify-end mb-7">
          <button
            onClick={() => navigate('/profile/settings')}
            className="w-9 h-9 flex items-center justify-center text-gold-400 rounded-full hover:bg-white/5 active:scale-90 transition-all cursor-pointer"
          >
            <i className="ri-settings-3-line text-lg"></i>
          </button>
        </div>

        {/* User Info */}
        <button
          onClick={() => navigate('/profile/verify')}
          className="relative flex items-center gap-4 text-left cursor-pointer group"
        >
          <div className="w-16 h-16 rounded-full bg-gold-500 flex items-center justify-center relative">
            <span className="w-8 h-8 flex items-center justify-center text-brown-950">
              <i className="ri-user-fill text-2xl"></i>
            </span>
            <span className="absolute bottom-0 right-0 w-5 h-5 rounded-full bg-white flex items-center justify-center">
              <i className="ri-vip-crown-fill text-gold-600 text-xs"></i>
            </span>
          </div>
          <div className="flex-1">
            <h2 className="text-white text-lg font-bold break-all">
              {displayName || '加载中...'}
            </h2>
            <div className="flex items-center gap-2 mt-1.5">
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                profile?.verified
                  ? 'bg-gold-500/20 text-gold-400'
                  : 'bg-red-500/20 text-red-400'
              }`}>
                {profile?.verified ? '已实名' : '未认证'}
              </span>
              <span className="bg-gold-500/20 text-gold-400 text-[10px] px-2 py-0.5 rounded-full">
                {profile?.member_level || 'VIP会员'}
              </span>
            </div>
          </div>
          <span className="w-5 h-5 flex items-center justify-center text-gold-400/60 group-hover:translate-x-0.5 transition-transform">
            <i className="ri-arrow-right-s-line text-lg"></i>
          </span>
        </button>
      </div>

      {/* Quick Stats */}
      <div className="px-3 -mt-5 relative z-10 animate-fade-up">
        <div className="bg-white rounded-2xl p-5 grid grid-cols-3 shadow-sm">
          {stats.map((stat, idx) => (
            <div
              key={stat.label}
              className={`text-center ${idx !== 0 ? 'border-l border-brown-50' : ''}`}
            >
              <p className="text-gold-600 text-xl font-bold">
                {stat.value}
                <span className="text-xs font-normal text-brown-400 ml-0.5">{stat.unit}</span>
              </p>
              <p className="text-brown-500 text-xs mt-1">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Asset Card */}
      <div className="px-3 mt-4 animate-fade-up" style={{ animationDelay: '60ms' }}>
        <div className="relative bg-gradient-to-r from-gold-400 to-gold-600 rounded-2xl p-5 overflow-hidden">
          <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-white/20"></div>
          <div className="relative">
            <div className="flex items-center justify-between mb-1">
              <p className="text-brown-800 text-xs font-medium">账户总资产</p>
              <span className="bg-brown-950/20 text-brown-900 text-[10px] px-2 py-0.5 rounded-full">
                实时
              </span>
            </div>
            <p className="text-brown-950 text-3xl font-bold tracking-tight">
              ¥{totalAssets.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            {assetChange !== null && (
              <span className={`inline-flex items-center text-xs font-bold mt-1 ${assetChange >= 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                {assetChange >= 0 ? <i className="ri-arrow-up-line mr-0.5"></i> : <i className="ri-arrow-down-line mr-0.5"></i>}
                ¥{Math.abs(assetChange).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            )}
            <div className="flex items-center justify-between mt-4">
              <span className="text-brown-800 text-xs">
                可用 ¥{(liveAssets.available || 0).toLocaleString('zh-CN', { minimumFractionDigits: 2 })} · 实物 ¥{(liveAssets.physicalValue || 0).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
              </span>
              <button
                onClick={() => navigate('/profile/vault')}
                className="bg-brown-950/20 hover:bg-brown-950/30 text-brown-900 text-xs px-4 py-1.5 rounded-full cursor-pointer whitespace-nowrap active:scale-95 transition-all"
              >
                查看金库
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="px-3 mt-4 animate-fade-up" style={{ animationDelay: '90ms' }}>
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <div className="grid grid-cols-3 gap-3">
            {quickActions.map((action) => (
              <button
                key={action.path}
                onClick={() => navigate(action.path)}
                className="flex flex-col items-center gap-2 cursor-pointer active:scale-95 transition-transform"
              >
                <div className="w-12 h-12 rounded-xl bg-gold-50 flex items-center justify-center">
                  <span className="w-6 h-6 flex items-center justify-center text-gold-600">
                    <i className={`${action.icon} text-xl`}></i>
                  </span>
                </div>
                <span className="text-brown-800 text-xs font-medium">{action.label}</span>
                <span className="text-brown-400 text-[10px]">{action.desc}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Menu */}
      <div className="px-3 mt-4 animate-fade-up" style={{ animationDelay: '120ms' }}>
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          {menuItems.map((item, idx) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center justify-between px-4 py-4 text-left cursor-pointer active:bg-brown-50 transition-colors ${
                idx !== menuItems.length - 1 ? 'border-b border-brown-50' : ''
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gold-50 flex items-center justify-center flex-shrink-0">
                  <span className="w-5 h-5 flex items-center justify-center text-gold-600">
                    <i className={`${item.icon} text-lg`}></i>
                  </span>
                </div>
                <div className="text-left">
                  <span className="text-brown-800 text-sm font-medium block">{item.label}</span>
                  <span className="text-brown-400 text-xs">
                    {item.label === '实名认证'
                      ? (profile?.verified ? '已认证' : '未认证')
                      : item.desc}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {item.verifiedBadge && (
                  <span
                    className={`text-[10px] px-2 py-0.5 rounded-full ${
                      profile?.verified
                        ? 'bg-gold-100 text-gold-700'
                        : 'bg-red-100 text-red-600'
                    }`}
                  >
                    {profile?.verified ? '已认证' : '未认证'}
                  </span>
                )}
                <span className="w-4 h-4 flex items-center justify-center text-brown-300">
                  <i className="ri-arrow-right-s-line"></i>
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Logout */}
      <div className="px-3 mt-4 mb-6 animate-fade-up" style={{ animationDelay: '180ms' }}>
        <button
          onClick={() => setShowLogout(true)}
          className="w-full py-3.5 rounded-2xl bg-white text-red-500 text-sm font-medium shadow-sm cursor-pointer whitespace-nowrap active:scale-[0.98] transition-transform"
        >
          退出登录
        </button>
      </div>

      {/* Logout Confirm */}
      <BottomSheet open={showLogout} onClose={() => setShowLogout(false)} title="退出登录">
        <div className="px-4 pb-8">
          <div className="text-center py-5">
            <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-3">
              <span className="w-7 h-7 flex items-center justify-center text-red-500">
                <i className="ri-logout-box-r-line text-2xl"></i>
              </span>
            </div>
            <p className="text-brown-900 text-base font-semibold">确定要退出当前账号吗？</p>
            <p className="text-brown-400 text-xs mt-1">退出后需重新登录才能继续操作</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowLogout(false)}
              className="flex-1 py-3 rounded-full border border-brown-200 text-brown-700 text-sm font-medium cursor-pointer whitespace-nowrap active:scale-[0.98] transition-transform"
            >
              取消
            </button>
            <button
              onClick={handleLogout}
              className="flex-1 py-3 rounded-full bg-red-500 text-white text-sm font-semibold cursor-pointer whitespace-nowrap active:scale-[0.98] transition-transform"
            >
              退出登录
            </button>
          </div>
        </div>
      </BottomSheet>
    </div>
  );
}